import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx-js-style';
import { InfobipReportAnalysisService } from './infobip-report-analysis.service';
import {
  BillingSubtotal,
  InfobipExportContext,
  InfobipReportView
} from './infobip-report.models';

type CellValue = string | number;

@Injectable({ providedIn: 'root' })
export class InfobipReportExportService {
  constructor(private readonly analysis: InfobipReportAnalysisService) {}

  download(view: InfobipReportView, context: InfobipExportContext): void {
    XLSX.writeFile(this.buildWorkbook(view, context), this.buildFilename(context), {
      bookType: 'xlsx',
      compression: true
    });
  }

  buildWorkbook(view: InfobipReportView, context: InfobipExportContext): XLSX.WorkBook {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, this.summarySheet(view, context), 'Resumen general');
    XLSX.utils.book_append_sheet(workbook, this.clientSheet(view), 'Facturación cliente');
    XLSX.utils.book_append_sheet(workbook, this.smsSheet(view), 'Detalle SMS');
    XLSX.utils.book_append_sheet(workbook, this.whatsappSheet(view), 'Detalle WhatsApp');
    XLSX.utils.book_append_sheet(workbook, this.countryOperatorSheet(view), 'País y operador');
    return workbook;
  }

  buildFilename(context: InfobipExportContext): string {
    return `infobip-facturacion_${context.startDate ?? 'todos'}_${context.endDate ?? 'todos'}.xlsx`;
  }

  private summarySheet(view: InfobipReportView, context: InfobipExportContext): XLSX.WorkSheet {
    const filters = context.filters;
    const rows: CellValue[][] = [
      ['Reporte de facturación Infobip'],
      [],
      ['Período desde', context.startDate ?? 'Todos'],
      ['Período hasta', context.endDate ?? 'Todos'],
      ['Cliente', filters.account ?? 'Todos'],
      ['Servicio', filters.service ?? 'Todos'],
      ['Tipo de tráfico', filters.trafficType ?? 'Todos'],
      ['Dirección', filters.direction ?? 'Todas'],
      ['País', filters.country ?? 'Todos'],
      ['Operador', filters.operator ?? 'Todos'],
      ['Generado', this.formatGeneratedAt(context.generatedAt)],
      [],
      ['RESUMEN', 'Valor'],
      ['Facturación total USD', this.number(view.summary.totalAmount)],
      ['Facturación SMS USD', this.number(view.summary.smsAmount)],
      ['Facturación WhatsApp USD', this.number(view.summary.whatsappAmount)],
      ['Partes SMS', this.number(view.summary.smsQuantity)],
      ['Mensajes WhatsApp', this.number(view.summary.whatsappQuantity)],
      ['Clientes con consumo', view.summary.clientCount],
      ['Registros', view.summary.recordCount],
      [],
      ['TOTALES POR CLIENTE', 'Partes SMS', 'Mensajes WhatsApp', 'Facturación SMS', 'Facturación WhatsApp', 'Facturación total']
    ];
    const tableHeaderRow = rows.length - 1;
    for (const client of view.clients) {
      rows.push([
        client.accountName,
        this.number(client.smsQuantity),
        this.number(client.whatsappQuantity),
        this.number(client.smsAmount),
        this.number(client.whatsappAmount),
        this.number(client.totalAmount)
      ]);
    }

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
    worksheet['!cols'] = [{ wch: 30 }, { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 24 }, { wch: 22 }];
    this.styleTitle(worksheet, 6);
    this.styleHeader(worksheet, 12, 2);
    this.styleHeader(worksheet, tableHeaderRow, 6);
    this.styleDataRange(worksheet, tableHeaderRow + 1, rows.length - 1, 6, [1, 2], [3, 4, 5]);
    worksheet['!autofilter'] = { ref: `A${tableHeaderRow + 1}:F${rows.length}` };
    return worksheet;
  }

  private clientSheet(view: InfobipReportView): XLSX.WorkSheet {
    const rows: CellValue[][] = [[
      'Tipo de fila', 'Cliente', 'Partes SMS', 'Mensajes WhatsApp', 'Facturación SMS',
      'Facturación WhatsApp', 'Importe detalle', 'Importe total general'
    ]];
    for (const client of view.clients) {
      rows.push([
        'DETALLE', client.accountName, this.number(client.smsQuantity), this.number(client.whatsappQuantity),
        this.number(client.smsAmount), this.number(client.whatsappAmount), this.number(client.totalAmount), ''
      ]);
    }
    rows.push([
      'TOTAL GENERAL', '', this.number(view.summary.smsQuantity), this.number(view.summary.whatsappQuantity),
      this.number(view.summary.smsAmount), this.number(view.summary.whatsappAmount), '', this.number(view.summary.totalAmount)
    ]);
    return this.typedSheet(rows, [24, 32, 18, 22, 20, 24, 20, 23], [2, 3], [4, 5, 6, 7]);
  }

  private smsSheet(view: InfobipReportView): XLSX.WorkSheet {
    const rows: CellValue[][] = [[
      'Tipo de fila', 'Fecha', 'Cliente', 'País', 'Operador', 'Precio unitario',
      'Cantidad detalle', 'Importe detalle', 'Cantidad subtotal', 'Importe subtotal',
      'Cantidad total general', 'Importe total general'
    ]];
    const subtotals = new Map(view.smsClientSubtotals.map(row => [row.accountName, row]));
    const details = view.records.filter(row => row.service === 'SMS')
      .sort((left, right) => `${left.accountName}\u0001${left.usageDay}`.localeCompare(`${right.accountName}\u0001${right.usageDay}`, 'es'));
    let currentClient = '';
    for (const row of details) {
      if (currentClient && currentClient !== row.accountName) {
        rows.push(this.smsSubtotalRow(subtotals.get(currentClient)!));
      }
      currentClient = row.accountName;
      rows.push([
        'DETALLE', row.usageDay, row.accountName, row.country, row.operator, this.number(row.unitPriceValue),
        this.number(row.quantityValue), this.number(row.totalAmountValue), '', '', '', ''
      ]);
    }
    if (currentClient) rows.push(this.smsSubtotalRow(subtotals.get(currentClient)!));
    rows.push([
      'TOTAL GENERAL', '', '', '', '', '', '', '', '', '',
      this.number(view.summary.smsQuantity), this.number(view.summary.smsAmount)
    ]);
    return this.typedSheet(rows, [25, 16, 30, 22, 28, 18, 20, 20, 21, 20, 25, 24], [6, 8, 10], [7, 9, 11], [5]);
  }

  private whatsappSheet(view: InfobipReportView): XLSX.WorkSheet {
    const rows: CellValue[][] = [[
      'Tipo de fila', 'Fecha', 'Cliente', 'Dirección', 'Tipo de tráfico', 'País', 'Operador', 'Precio unitario',
      'Cantidad detalle', 'Importe detalle', 'Cantidad subtotal', 'Importe subtotal',
      'Cantidad total general', 'Importe total general'
    ]];
    const trafficSubtotals = new Map(view.whatsappTrafficSubtotals.map(row => [`${row.accountName}\u0001${row.trafficType}`, row]));
    const clientSubtotals = new Map(view.whatsappClientSubtotals.map(row => [row.accountName, row]));
    const details = view.records.filter(row => row.service === 'WHATSAPP')
      .sort((left, right) => [left.accountName, left.normalizedTrafficType, left.usageDay].join('\u0001')
        .localeCompare([right.accountName, right.normalizedTrafficType, right.usageDay].join('\u0001'), 'es'));
    let currentClient = '';
    let currentTraffic = '';
    for (const row of details) {
      if (currentTraffic && (currentClient !== row.accountName || currentTraffic !== row.normalizedTrafficType)) {
        rows.push(this.whatsappSubtotalRow(trafficSubtotals.get(`${currentClient}\u0001${currentTraffic}`)!, true));
      }
      if (currentClient && currentClient !== row.accountName) {
        rows.push(this.whatsappSubtotalRow(clientSubtotals.get(currentClient)!, false));
      }
      currentClient = row.accountName;
      currentTraffic = row.normalizedTrafficType;
      rows.push([
        'DETALLE', row.usageDay, row.accountName, row.normalizedDirection, row.normalizedTrafficType, row.country, row.operator,
        this.number(row.unitPriceValue), this.number(row.quantityValue), this.number(row.totalAmountValue), '', '', '', ''
      ]);
    }
    if (currentTraffic) rows.push(this.whatsappSubtotalRow(trafficSubtotals.get(`${currentClient}\u0001${currentTraffic}`)!, true));
    if (currentClient) rows.push(this.whatsappSubtotalRow(clientSubtotals.get(currentClient)!, false));
    rows.push([
      'TOTAL GENERAL', '', '', '', '', '', '', '', '', '', '', '',
      this.number(view.summary.whatsappQuantity), this.number(view.summary.whatsappAmount)
    ]);
    return this.typedSheet(rows, [28, 16, 30, 17, 20, 22, 28, 18, 20, 20, 21, 20, 25, 24], [8, 10, 12], [9, 11, 13], [7]);
  }

  private countryOperatorSheet(view: InfobipReportView): XLSX.WorkSheet {
    const rows: CellValue[][] = [[
      'Tipo de fila', 'País', 'Operador', 'Servicio', 'Unidad',
      'Cantidad detalle', 'Importe detalle', 'Cantidad total general', 'Importe total general'
    ]];
    for (const row of view.countryOperatorRows) {
      rows.push([
        'DETALLE', row.country, row.operator, row.service, row.unitType,
        this.number(row.quantity), this.number(row.amount), '', ''
      ]);
    }
    // SMS parts and WhatsApp messages are different units and must never be combined.
    rows.push(['TOTAL GENERAL', '', '', '', '', '', '', '', this.number(view.summary.totalAmount)]);
    return this.typedSheet(rows, [24, 22, 28, 18, 20, 20, 20, 25, 24], [5, 7], [6, 8]);
  }

  private smsSubtotalRow(row: BillingSubtotal): CellValue[] {
    return ['SUBTOTAL CLIENTE', '', row.accountName, '', '', '', '', '', this.number(row.quantity), this.number(row.amount), '', ''];
  }

  private whatsappSubtotalRow(row: BillingSubtotal, traffic: boolean): CellValue[] {
    return [
      traffic ? 'SUBTOTAL CLIENTE/TRÁFICO' : 'SUBTOTAL CLIENTE',
      '', row.accountName, '', traffic ? row.trafficType ?? '' : '', '', '', '', '', '',
      this.number(row.quantity), this.number(row.amount), '', ''
    ];
  }

  private typedSheet(
    rows: CellValue[][],
    widths: number[],
    quantityColumns: number[],
    amountColumns: number[],
    priceColumns: number[] = []
  ): XLSX.WorkSheet {
    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!cols'] = widths.map(wch => ({ wch }));
    worksheet['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(widths.length - 1)}${rows.length}` };
    this.styleHeader(worksheet, 0, widths.length);
    this.styleDataRange(worksheet, 1, rows.length - 1, widths.length, quantityColumns, amountColumns, priceColumns);
    for (let rowIndex = 1; rowIndex < rows.length; rowIndex += 1) {
      this.styleTypedRow(worksheet, rowIndex, widths.length, String(rows[rowIndex][0]));
    }
    return worksheet;
  }

  private styleTitle(worksheet: XLSX.WorkSheet, columnCount: number): void {
    this.applyStyle(worksheet, 'A1', {
      font: { bold: true, sz: 16, color: { rgb: '17365D' } },
      alignment: { horizontal: 'left', vertical: 'center' }
    });
    for (let column = 0; column < columnCount; column += 1) {
      this.applyStyle(worksheet, XLSX.utils.encode_cell({ r: 0, c: column }), {
        font: { bold: true, sz: 16, color: { rgb: '17365D' } }
      });
    }
  }

  private styleHeader(worksheet: XLSX.WorkSheet, row: number, columnCount: number): void {
    for (let column = 0; column < columnCount; column += 1) {
      this.applyStyle(worksheet, XLSX.utils.encode_cell({ r: row, c: column }), {
        font: { bold: true, color: { rgb: 'FFFFFF' } },
        fill: { patternType: 'solid', fgColor: { rgb: '1F4E78' } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: this.thinBorder()
      });
    }
  }

  private styleDataRange(
    worksheet: XLSX.WorkSheet,
    startRow: number,
    endRow: number,
    columnCount: number,
    quantityColumns: number[],
    amountColumns: number[],
    priceColumns: number[] = []
  ): void {
    for (let row = startRow; row <= endRow; row += 1) {
      for (let column = 0; column < columnCount; column += 1) {
        const numFmt = priceColumns.includes(column)
          ? '#,##0.00000000'
          : amountColumns.includes(column)
          ? '$#,##0.000000'
          : quantityColumns.includes(column) ? '#,##0.######' : undefined;
        this.applyStyle(worksheet, XLSX.utils.encode_cell({ r: row, c: column }), {
          alignment: { horizontal: numFmt ? 'right' : 'left', vertical: 'center' },
          border: this.thinBorder(),
          ...(numFmt ? { numFmt } : {})
        });
      }
    }
  }

  private styleTypedRow(worksheet: XLSX.WorkSheet, row: number, columnCount: number, type: string): void {
    if (type === 'DETALLE') return;
    const total = type === 'TOTAL GENERAL';
    for (let column = 0; column < columnCount; column += 1) {
      const address = XLSX.utils.encode_cell({ r: row, c: column });
      const cell = worksheet[address];
      if (!cell) continue;
      cell.s = {
        ...(cell.s ?? {}),
        font: { bold: true, color: { rgb: total ? 'FFFFFF' : '17365D' } },
        fill: { patternType: 'solid', fgColor: { rgb: total ? '17365D' : 'D9EAF7' } },
        border: total
          ? { top: { style: 'double', color: { rgb: '17365D' } } }
          : this.thinBorder()
      };
    }
  }

  private applyStyle(worksheet: XLSX.WorkSheet, address: string, style: object): void {
    const cell = worksheet[address];
    if (cell) cell.s = style;
  }

  private thinBorder() {
    return { bottom: { style: 'thin', color: { rgb: 'D9E2F3' } } };
  }

  private number(value: { coefficient: bigint; scale: number }): number {
    return this.analysis.toNumber(value);
  }

  private formatGeneratedAt(value: Date): string {
    return new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short' }).format(value);
  }
}
